import { PORTAL_TOAST_ID } from '@app/constants';
import Toast from '@app/domains/common/components/Toast';
import styles from '@app/domains/common/hooks/styles.module.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type Root, createRoot } from 'react-dom/client';

type UseToastParam = {
    openingDuration?: number; // ms
    fadeoutDuration?: number; // ms
};

export default function useToast({ openingDuration = 3000, fadeoutDuration = 300 }: UseToastParam) {
    const rootRef = useRef<Element | null>();
    const transitionRef = useRef<HTMLDivElement>(null);
    const [mounted, setMounted] = useState(false);

    const remove = useCallback(
        (root: Root) => {
            if (rootRef.current && mounted) {
                if (transitionRef.current) {
                    transitionRef.current.classList.remove(styles['slide-up']);
                    transitionRef.current.classList.add(styles['slide-down']);
                    transitionRef.current.onanimationend = () => {
                        root.unmount();
                    };
                }
            }
        },
        [mounted]
    );

    useEffect(() => {
        setMounted(true);
        if (document) {
            const toastRoot = document.getElementById(PORTAL_TOAST_ID);
            rootRef.current = toastRoot;
        }
    }, [openingDuration, remove]);

    const open = ({ text }: { text: string }) => {
        if (rootRef.current && mounted) {
            const root = createRoot(rootRef.current);
            root.render(
                <Toast
                    callback={() => {
                        if (openingDuration) setTimeout(() => remove(root), openingDuration);
                    }}
                    transitionRef={transitionRef}
                    fadeoutDuration={fadeoutDuration}
                    text={text}
                />
            );
        }
    };

    return {
        open,
    };
}
